import React, { useEffect, useState } from 'react';
import { Tree, Spin, Avatar } from 'antd';
import { ShopOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import type { TreeDataNode } from 'antd';
import { type MenuItem, type IconConfig } from '@/api/services/menu.service';
import { useTranslation } from 'react-i18next';
import { useMenu } from '@/context/MenuContext';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Icons from '@ant-design/icons';
import './MenuTree.css';

/**
 * Get icon component from icon name
 */
const getIcon = (icon?: string | IconConfig) => {
  if (!icon) return null;

  let iconName: string;
  let iconColor: string | undefined;
  let iconTheme: 'fill' | 'outline' | undefined;

  if (typeof icon === 'string') {
    iconName = icon;
  } else {
    iconName = icon.icon_type;
    iconColor = icon.icon_color;
    iconTheme = icon.icon_theme;
  }

  // Convert icon name to PascalCase with "Outlined" or "Filled" suffix
  const suffix = iconTheme === 'fill' ? 'Filled' : 'Outlined';
  const iconComponentName =
    iconName.charAt(0).toUpperCase() + iconName.slice(1) + suffix;

  // Get icon component from Icons namespace
  const IconComponent = (Icons as any)[iconComponentName];

  if (IconComponent) {
    return <IconComponent style={{ color: iconColor }} />;
  }

  return null;
};

/**
 * Convert MenuItem to TreeDataNode
 */
const convertToTreeData = (
  items: MenuItem[],
  t: any,
  itemsMap: Map<React.Key, MenuItem>
): TreeDataNode[] => {
  const result: TreeDataNode[] = [];

  items.forEach((item) => {
    // Store item in map
    itemsMap.set(item.code, item);

    // If label is not visible, flatten children to root level
    if (item.label_visible === false && item.children) {
      // Add children directly without the parent node
      const childNodes = convertToTreeData(item.children, t, itemsMap);
      result.push(...childNodes);
    } else {
      // Get label from translation using code
      const label = t(`menu.${item.code}`, item.code);

      const node: TreeDataNode = {
        key: item.code,
        title: label,
        icon: getIcon(item.icon),
        children: item.children
          ? convertToTreeData(item.children, t, itemsMap)
          : undefined,
        // Items are selectable if they have a URL (even with children) or if they're not section headers
        // Section headers (collapsable: false) should not be selectable
        selectable:
          item.collapsable === false
            ? false
            : item.url
              ? true
              : item.children
                ? true
                : true,
        // Add class for styling
        className:
          item.collapsable === false ? 'menu-section-header' : undefined,
      };

      result.push(node);
    }
  });

  return result;
};

interface MenuTreeProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export const MenuTree: React.FC<MenuTreeProps> = ({
  collapsed = false,
  onCollapsedChange,
}) => {
  const { t } = useTranslation();
  const { menuItems, loading, error } = useMenu();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuData, setMenuData] = useState<TreeDataNode[]>([]);
  const [menuItemsMap, setMenuItemsMap] = useState<Map<React.Key, MenuItem>>(
    new Map()
  );
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  // Get tenant code from hostname
  const getTenantCode = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    return parts[0].toUpperCase();
  };

  const tenantCode = getTenantCode();
  const tenantLogoUrl = `/v2/api/tenants/code/${tenantCode}/logo.png`;

  // Helper function to get all keys from tree data with specific map
  const getAllKeysWithMap = (
    data: TreeDataNode[],
    itemsMap: Map<React.Key, MenuItem>
  ): React.Key[] => {
    const keys: React.Key[] = [];
    const extractKeys = (nodes: TreeDataNode[]) => {
      nodes.forEach((node) => {
        // Add ALL keys for items with children (including non-collapsable section headers)
        // Non-collapsable items need to be expanded to show their children
        if (node.children) {
          keys.push(node.key);
          extractKeys(node.children);
        }
      });
    };
    extractKeys(data);
    return keys;
  };

  // Get keys that should always be expanded (non-collapsable items)
  const getAlwaysExpandedKeys = (data: TreeDataNode[]): React.Key[] => {
    const keys: React.Key[] = [];
    const extractKeys = (nodes: TreeDataNode[]) => {
      nodes.forEach((node) => {
        const menuItem = menuItemsMap.get(node.key);
        if (node.children && menuItem?.collapsable === false) {
          keys.push(node.key);
          extractKeys(node.children);
        } else if (node.children) {
          extractKeys(node.children);
        }
      });
    };
    extractKeys(data);
    return keys;
  };

  useEffect(() => {
    console.log('MenuTree useEffect - menuItems:', menuItems);
    if (menuItems.length > 0) {
      // Create new map for menu items
      const newItemsMap = new Map<React.Key, MenuItem>();
      const treeData = convertToTreeData(menuItems, t, newItemsMap);

      console.log('MenuTree - treeData:', treeData);

      // Get all keys using the new map
      const allKeys = getAllKeysWithMap(treeData, newItemsMap);
      console.log('MenuTree - expandedKeys will be:', allKeys);

      setMenuItemsMap(newItemsMap);
      setMenuData(treeData);
      setExpandedKeys(allKeys);
    }
  }, [menuItems, t]);

  const handleExpand = (keys: React.Key[]) => {
    // Always keep non-collapsable items expanded
    const alwaysExpanded = getAlwaysExpandedKeys(menuData);
    const mergedKeys = Array.from(new Set([...keys, ...alwaysExpanded]));
    setExpandedKeys(mergedKeys);
  };

  const toggleCollapse = () => {
    onCollapsedChange?.(!collapsed);
  };

  const handleSelect = (_selectedKeys: React.Key[], info: any) => {
    const selectedNode = info.node;
    const menuItem = menuItemsMap.get(selectedNode.key);

    if (menuItem?.url) {
      // Only navigate if we're not already on this URL
      // This prevents unnecessary re-renders and router issues
      if (location.pathname !== menuItem.url) {
        navigate(menuItem.url, { replace: false });
      }
    }
  };

  if (loading) {
    return (
      <div className="menu-tree-loading">
        <Spin />
      </div>
    );
  }

  if (error) {
    return <div className="menu-tree-error">{error}</div>;
  }

  return (
    <div className="menu-tree-container">
      {/* Tenant Header */}
      <div className={`menu-tenant-header ${collapsed ? 'collapsed' : ''}`}>
        {!collapsed && (
          <div className="menu-tenant-info">
            <img
              src={tenantLogoUrl}
              alt={`${tenantCode} logo`}
              className="menu-tenant-logo"
              onError={(e) => {
                // Fallback to icon if logo fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <Avatar
              size={32}
              icon={<ShopOutlined />}
              style={{ backgroundColor: '#667eea', display: 'none' }}
              className="menu-tenant-logo-fallback"
            />
            <span className="menu-tenant-name">{tenantCode}</span>
          </div>
        )}
        {/* Toggle button removed - now controlled by FloatingNavBar */}
      </div>

      {/* Menu Tree */}
      {!collapsed && (
        <div className="menu-tree-content">
          <Tree
            showIcon
            showLine={false}
            expandedKeys={expandedKeys}
            onExpand={handleExpand}
            treeData={menuData}
            onSelect={handleSelect}
            switcherIcon={({ expanded }) =>
              expanded ? <UpOutlined /> : <DownOutlined />
            }
          />
        </div>
      )}
    </div>
  );
};
